import { Routes, Route } from 'react-router-dom';
import { HistoryRouter } from 'redux-first-history/rr6';
import { Provider } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PkceProvider, PkceSecure, UserSessionComponent } from '@oliasoft/pkce';
import '@oliasoft-open-source/react-ui-library/dist/global.css';
import { Toaster, Grid, Text, toast, dismissToast } from '@oliasoft-open-source/react-ui-library';
import { store, history } from 'store/configureStore';
import translations from 'src/internationalisation/translation-map.json';
import '../internationalisation/i18n.js';
import { routes } from 'routes/routes';
import { Companies } from 'views/companies/companies';
import { Hierarchy } from 'views/hierarchy/hierarchy';
import { Project } from 'views/project/project';
import { NotFound } from 'views/not-found/not-found';
import { UserSettings } from 'views/settings/user-settings/user-settings';
import { OperationCodes } from 'views/settings/company-settings/operation-codes/operation-codes';
import { UserTemplates } from 'src/views/settings/user-settings/user-templates/user-templates.jsx';
import { CompareEstimates } from 'views/compare-estimates/compare-estimates';
import { Campaigns } from 'views/campaigns/campaigns.jsx';
import { SocketProvider } from 'src/common/socket-contex/socket-context';
import ClientConfig from 'src/config/config';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { CompanySettings } from 'views/settings/company-settings/company-settings.jsx';
import { CurrencyWrapper } from 'views/settings/company-settings/currency/currency-wrapper.jsx';
import { TimeTracker } from 'views/settings/company-settings/time-tracker/time-tracker.jsx';
import { UsageTracker } from 'src/analytics/usage-tracker/usage-tracker.tsx';
import { generateMixpanelSessionID } from 'src/analytics/mixpanel/mixpanel-client';
import { SentryConfiguration } from 'src/common/error-boundary/sentry-configuration.tsx';
import { MaintenancePage } from 'src/common/components/maintenance-page/MaintenancePage.tsx';
import { Main } from './navigation/main/main';
import { UserKpi } from './settings/user-settings/user-kpi/user-kpi.jsx';
import { CompanyKpi } from './settings/company-settings/company-kpi/company-kpi.jsx';
import { CompanyTemplates } from './settings/company-settings/company-templates/company-templates.jsx';
import { SectionsDataStructure } from './settings/company-settings/sections-data-structure/sections-data-structure.tsx';
import './theme.css';
import { PriceBook } from './settings/company-settings/price-book/price-book.tsx';
import { Hierarchy as HierarchySettings } from './settings/company-settings/hierarchy/hierarchy.tsx';
import PortalWithAuth from './portal/portal-with-auth';
import { RigOperationTemplate } from './settings/company-settings/rig-operations-templates/rig-operation-template.tsx';
import { HierarchyPermissions } from './settings/company-settings/hierarchy/hierarchy-permissions/hierarchy-permissions.tsx';
import { HierarchyOptionalParameters } from './settings/company-settings/hierarchy/hierarchy-optional-parameters/hierarchy-optional-parameters.tsx';
import { CostCodes } from './settings/company-settings/cost-codes/cost-codes.tsx';
import { NewAppVersionProvider } from './navigation/new-app-version/new-app-version-provider.tsx';
import { ReleaseNotesProvider } from './navigation/new-app-version/release-notes-provider.tsx';
import FeatureFlagsComponent from './settings/feature-flags/feature-flags.tsx';
import { CostSetup as CompanyCostSetup } from './settings/company-settings/cost-setup/cost-setup.tsx';
import TestSentry from './sentry/test-sentry.tsx';
import { FeatureFlagName } from 'src/enums/feature-flags.ts';

const App = () => {
  const { t } = useTranslation();
  const { enableSettingsMaintenance } = ClientConfig.config;
  const getUserSessionComponent = () => {
    return (
      ClientConfig.config.enableUserSessionComponent && (
        <UserSessionComponent
          translations={{
            session_expiring: t(translations.userSession_session_expiring),
            session_expires_soon: t(translations.userSession_session_expires_soon),
            session_expires_details_first_part: t(translations.userSession_session_expires_details_first_part),
            session_expires_details_second_part: t(
              translations.userSession_session_expires_refresh_token_details_second_part,
            ),
            minutes_noun: t(translations.userSession_minutes_noun).toLowerCase(),
          }}
          clientConfig={{
            pkceProvider: ClientConfig.config.pkce.provider,
            env: ClientConfig.config.env,
            clientId: ClientConfig.config.applicationId,
            idpServerUrl: ClientConfig.config.idpServerUrl,
            enableSessionMonitor: ClientConfig.config.enableSessionMonitor,
          }}
          components={{
            Grid,
            Text,
            toast,
            dismissToast,
          }}
        />
      )
    );
  };

  generateMixpanelSessionID();

  return (
    <Provider store={store}>
      <PkceProvider
        provider={ClientConfig.config.pkce.provider}
        renewTokenMode={ClientConfig.config.pkce.renewTokenMode}
        clientId={ClientConfig.config.applicationId}
        idpServerUrl={ClientConfig.config.idpServerUrl}
        scope={`openid profile email ${ClientConfig.config.applicationId}`}
        hostBaseUrl={ClientConfig.config.hostBaseUrl}
        history={history}
      >
        <HistoryRouter history={history}>
          {/** If no other error boundary catches the crash, then this will... */}
          <UsageTracker /> {/* Mixpanel tracker */}
          <SentryConfiguration>
            <PortalWithAuth />
            <Routes>
              <Route
                index
                element={
                  <PkceSecure>
                    {getUserSessionComponent()}
                    <Companies />
                  </PkceSecure>
                }
              />
              <Route
                {...routes.root.pattern}
                element={
                  <PkceSecure>
                    <ReleaseNotesProvider>
                      <NewAppVersionProvider>
                        <SocketProvider>
                          {getUserSessionComponent()}
                          <Main />
                        </SocketProvider>
                      </NewAppVersionProvider>
                    </ReleaseNotesProvider>
                  </PkceSecure>
                }
              >
                <Route {...routes.hierarchy.pattern} element={<Hierarchy />} />
                <Route {...routes.compare.pattern} element={<CompareEstimates />} />
                <Route {...routes.campaigns.pattern} element={<Campaigns />} />
                <Route {...routes.project.pattern} element={<Project />} />
                <Route {...routes.project.pattern} element={<Project />} />
                <Route
                  {...routes.userSettings.pattern}
                  element={enableSettingsMaintenance ? <MaintenancePage /> : <UserSettings />}
                >
                  <Route {...routes.userSettings.nested.templates.pattern} element={<UserTemplates />} />
                  <Route {...routes.userSettings.nested.kpi.pattern} element={<UserKpi />} />
                  <Route {...routes.userSettings.nested.featureFlags.pattern} element={<FeatureFlagsComponent />} />
                </Route>
                <Route
                  {...routes.companySettings.pattern}
                  element={enableSettingsMaintenance ? <MaintenancePage /> : <CompanySettings />}
                >
                  <Route {...routes.companySettings.nested.currency.pattern} element={<CurrencyWrapper />} />
                  <Route {...routes.companySettings.nested.hierarchy.pattern} element={<HierarchySettings />} />
                  <Route
                    {...routes.companySettings.nested.hierarchyPermissions.pattern}
                    element={<HierarchyPermissions />}
                  />
                  <Route
                    {...routes.companySettings.nested.hierarchyOptionalParameters.pattern}
                    element={<HierarchyOptionalParameters />}
                  />
                  <Route {...routes.companySettings.nested.operationCodes.pattern} element={<OperationCodes />} />
                  <Route {...routes.companySettings.nested.templates.pattern} element={<CompanyTemplates />} />
                  <Route {...routes.companySettings.nested.kpi.pattern} element={<CompanyKpi />} />
                  <Route {...routes.companySettings.nested.timeTracker.pattern} element={<TimeTracker />} />
                  <Route
                    {...routes.companySettings.nested.sectionsDataStructure.pattern}
                    element={<SectionsDataStructure />}
                  />
                  <Route {...routes.companySettings.nested.priceBook.pattern} element={<PriceBook />} />
                  <Route
                    {...routes.companySettings.nested.rigOperationTemplates.pattern}
                    element={<RigOperationTemplate />}
                  />
                  <Route {...routes.companySettings.nested.costCodes.pattern} element={<CostCodes />} />
                  <Route {...routes.companySettings.nested.costSetup.pattern} element={<CompanyCostSetup />} />
                </Route>
                {FeatureFlags.isActive(FeatureFlagName.SentryTestPage) ? (
                  <Route {...routes.sentryTest.pattern} element={<TestSentry />} />
                ) : null}
                <Route {...routes.sentryTest.pattern} element={<TestSentry />} />
                <Route {...routes.notFound.pattern} element={<NotFound />} />
              </Route>
            </Routes>
          </SentryConfiguration>
        </HistoryRouter>
      </PkceProvider>
      <Toaster />
    </Provider>
  );
};

export default App;
